import { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  createTemplate,
  deleteTemplate,
  duplicateTemplate,
} from "@apis/template";
import { AppContext } from "../context/AppContext";
import { Template } from "../type";
import NoTemplate from "@components/Dashboard/NoTemplate";
import TemplateItem from "@components/Dashboard/TemplateItem";
import AddTemplateModal from "@components/Modals/AddTemplateModal/AddTemplateModal";
import ConfirmModal from "@components/Modals/ConfirmModal";
import TemplateFilter from "@components/Dashboard/TemplateFilter";
import Sidebar from "@components/Sidebar";
import { ROUTE_EDIT } from "@constants/routes";
import { PlusIcon } from "@heroicons/react/24/solid";

function Dashboard() {
  const { templates, setTemplates } = useContext(AppContext);
  const [isLoading, setIsLoading] = useState(false);
  const [isConfirmModalShown, setIsConfirmModalShown] = useState(false);
  const [isAddTemplateModalShown, setIsAddTemplateModalShown] = useState(false);
  const [toRemoveTemplateId, setToRemoveTemplateId] = useState("");
  const [keyword, setKeyword] = useState("");
  const navigate = useNavigate();
  const filteredTemplates = getFilteredTemplates();

  function getFilteredTemplates() {
    if (!keyword) {
      return templates.sort(
        (a, b) =>
          new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
      );
    }
    return templates.filter((template) => {
      return template.title.toLowerCase().includes(keyword.toLowerCase());
    });
  }

  return (
    <div className="flex h-full flex-col bg-gradient-to-br from-gray-50 to-gray-100">
      {/* <Header /> removed for a cleaner, editor-style dashboard */}
      <div className="flex flex-1 overflow-hidden">
        <Sidebar activeItem="templates" />
        <main className="flex-1 overflow-y-auto px-8 py-10">
          <div className="mb-8 flex flex-col items-start justify-between gap-4 md:flex-row md:items-center">
            <h1 className="text-3xl font-extrabold tracking-tight text-gray-900">
              Templates
            </h1>
            <div className="flex items-center space-x-2">
              <TemplateFilter
                onFilter={(newKeyword) => {
                  setKeyword(newKeyword);
                }}
              />
              <button
                className="ml-2 flex items-center rounded-lg bg-gray-900 px-4 py-2 text-white shadow-lg transition hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-400"
                onClick={() => setIsAddTemplateModalShown(true)}
              >
                <PlusIcon className="mr-2 h-5 w-5" />
                New Template
              </button>
            </div>
          </div>
          {filteredTemplates.length === 0 && (
            <div className="flex flex-col items-center justify-center py-32">
              <NoTemplate />
              <p className="mt-6 text-lg text-gray-500">
                No templates found. Start by creating a new template!
              </p>
              <button
                className="mt-6 flex items-center rounded-lg bg-gray-900 px-6 py-3 text-white shadow-lg transition hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-400"
                onClick={() => setIsAddTemplateModalShown(true)}
              >
                <PlusIcon className="mr-2 h-5 w-5" />
                New Template
              </button>
            </div>
          )}
          {filteredTemplates.length !== 0 && (
            <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
              {filteredTemplates.map((template) => (
                <TemplateItem
                  template={template}
                  key={template.id}
                  onRemove={async (templateId) => {
                    if (isLoading) return;
                    setToRemoveTemplateId(templateId);
                    setIsConfirmModalShown(true);
                  }}
                  onDuplicate={async (templateId) => {
                    if (isLoading) return;
                    setIsLoading(true);
                    toast.promise(duplicateTemplate({ templateId }), {
                      pending: "Duplicating template...",
                      success: {
                        render({ data }: { data: { data: Template } }) {
                          setIsLoading(false);
                          const foundTemplate = data?.data;
                          setTemplates([foundTemplate, ...templates]);
                          return "Template created!";
                        },
                      },
                      error: {
                        render({ data }) {
                          setIsLoading(false);
                          return (data as Error).message;
                        },
                      },
                    });
                  }}
                  onOpen={(templateId) => {
                    if (isLoading) return;
                    navigate(`${ROUTE_EDIT}/${templateId}`);
                  }}
                />
              ))}
            </div>
          )}
        </main>
      </div>
      <ConfirmModal
        header="Are you sure you want to delete this template?"
        open={isConfirmModalShown}
        onCancel={() => {
          setIsConfirmModalShown(false);
        }}
        onSubmit={() => {
          setIsConfirmModalShown(false);
          setIsLoading(true);
          const templateId = toRemoveTemplateId;
          toast
            .promise(deleteTemplate(templateId), {
              pending: "Removing template...",
              success: {
                render({ data }: { data: { data: {} } }) {
                  setTemplates((prevState) => {
                    return prevState.filter(
                      (prevTemplate) => prevTemplate.id !== templateId
                    );
                  });
                  return "Template removed!";
                },
              },
              error: {
                render({ data }) {
                  return (data as Error).message;
                },
              },
            })
            .finally(() => {
              setToRemoveTemplateId("");
              setIsLoading(false);
            });
        }}
      />
      <AddTemplateModal
        open={isAddTemplateModalShown}
        onCancel={() => {
          setIsAddTemplateModalShown(false);
        }}
        onSubmit={(templateInfo) => {
          setIsLoading(true);
          setIsAddTemplateModalShown(false);
          toast.promise(createTemplate(templateInfo), {
            pending: "Creating template...",
            success: {
              render({ data }: { data: { data: Template } }) {
                setIsLoading(false);
                const foundTemplate = data?.data;
                setTemplates([foundTemplate, ...templates]);
                return "Template created!";
              },
            },
            error: {
              render({ data }) {
                setIsLoading(false);
                return (data as Error).message;
              },
            },
          });
        }}
      />
    </div>
  );
}

export default Dashboard;
